import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Link,
  AppBar,
  Tooltip,
  Divider,
  Autocomplete,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid2";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  SETTING_API_05,
  SETTING_API_06,
  ITEM_API_01,
  ITEM_CREATE_VARIANT_TYPE,
} from "../../api/ApiModel.tsx";
import { PageDataContext, PageDataContextType } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useDropdownData } from "../../hooks/useDropdownData.tsx";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AppDialog from "../../components/common/AppDialog.tsx";

// For Variant Grids and Popups
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import DeleteIcon from "@mui/icons-material/Delete";
import AddVariantTypeDialog from "../../components/items/AddVariantTypeDialog.tsx";

// Types and Interfaces
interface UserParams {
  COMPCODE?: string;
  STAFFMAPLOCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface RootState {
  user: UserState;
}

interface ApiItem {
  itemcode?: string | number;
  itemname?: string;
  variantdata?: string;
  endate?: string;
  uptype?: string;
  sts?: string;
  modelname?: string;
  brandcode?: string | number;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  packagecode?: string | number;
  measuringunitcode?: string | number;
  uomcode?: string | number;
  packagingcode?: string | number;
  packageunits?: number;
  descrip?: string;
  imagetype?: string;
  productimgs?: string;
  docpathjson?: string;
  maincatname?: string;
  catname?: string;
  subcatname?: string;
  genericname?: string;
  brandname?: string;
  packagename?: string;
  measuringunit?: string;
  measuringunitname?: string;
  enuser?: string;
  [key: string]: any;
}

interface DropdownOption {
  label: string;
  value: string | number;
}

interface UOMOption extends DropdownOption {}
interface PackageOption extends DropdownOption {}

interface VariantTypeOption {
  code?: string | number;
  value?: string;
  label?: string;
  name?: string;
  VARIANTNAME?: string;
  variantname?: string;
  [key: string]: any;
}

interface VariantPair {
  variantName: string;
  value: string;
}

interface VariantData {
  [key: string]: string[];
}

interface VariantCombination {
  [key: string]: string;
}

interface ExistingVariantRow {
  id: string;
  index: number;
  variantDisplay: string;
}

interface LocationState {
  selectedItem?: ApiItem;
  returnFromItemImages?: boolean;
  variants?: Array<{ type: string; values: string[] }>;
  formData?: {
    modelName?: string;
    unitsPerPackage?: string;
    description?: string;
    uom?: string;
    packageType?: string;
  };
  imageType?: string;
  commonImages?: any[];    // ✅ NEW: forwarded from AddVariantPage
  itemcode?: string | number; // ✅ NEW
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

interface GridColumn {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  minWidth?: number;
  renderCell?: (params: any) => React.ReactNode;
}

// ✅ NEW: Fallback parser — extracts common images from selectedItem.productimgs
// if for any reason they weren't forwarded from AddVariantPage.
const parseCommonImagesFromItem = (item: ApiItem | null): any[] => {
  if (!item) return [];
  const raw = item.productimgs || item.docpathjson;
  if (!raw) return [];

  try {
    let jsonString: string = raw;
    if (typeof raw === "string") {
      jsonString = raw.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
      if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
        jsonString = jsonString.slice(1, -1);
      }
    }

    let parsed: any[] = [];
    try {
      parsed = JSON.parse(jsonString);
    } catch {
      try {
        parsed = JSON.parse(raw);
      } catch {
        if (typeof raw === "string" && raw.includes("[")) {
          const start = raw.indexOf("[");
          const end = raw.lastIndexOf("]") + 1;
          if (start >= 0 && end > start) {
            parsed = JSON.parse(raw.substring(start, end));
          }
        }
      }
    }

    if (!Array.isArray(parsed)) parsed = [parsed];

    return parsed
      .filter(Boolean)
      .map((img: any, idx: number) => {
        let imageUrl = "";
        if (img.FILEPATH && (img.FILEPATH.startsWith("http") || img.FILEPATH.startsWith("data:"))) {
          imageUrl = img.FILEPATH;
        } else if (img.bytarr) {
          imageUrl = `data:image/jpeg;base64,${img.bytarr}`;
        } else if (img.filepath || img.data) {
          imageUrl = img.filepath || img.data;
        }

        if (!imageUrl) return null;

        const rawIsDefault = img.ISDEFAULT ?? img.isdefault ?? img.IsDefault ?? false;
        const isDefault =
          String(rawIsDefault).toUpperCase() === "Y" ||
          rawIsDefault === 1 ||
          rawIsDefault === true;

        return {
          id: Date.now() + idx,
          data: imageUrl,
          name: img.filename || img.FILEPATH?.split("/").pop() || `image_${idx}.jpg`,
          size: 0,
          type: "image/jpeg",
          filename: img.filename || img.FILEPATH?.split("/").pop() || `image_${idx}.jpg`,
          base64Data: img.bytarr || "",
          filepath: img.FILEPATH || img.filepath || "",
          filepathUrl: imageUrl,
          doctypecode: img.DOCTYPECODE || "IMG",
          doctypeothername: img.DOCTYPEOTHERNAME || "Item Image",
          remark: img.REMARK || img.remark || "",
          variantIdentifier: "Common Image",
          rawData: img,
          isDefault,
          isExisting: true,
        };
      })
      .filter(Boolean);
  } catch (e) {
    console.error("Failed to parse common images from item:", e);
    return [];
  }
};

function CreateVariant(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme) as any;
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageData } = useContext(PageDataContext) as PageDataContextType;

  // ---------------- States ----------------
  const [genericName, setGenericName] = useState<string>("");
  const [modelName, setModelName] = useState<string>("");
  const [unitPerPackage, setUnitPerPackage] = useState<string>("");
  const [unitOfMeasure, setUnitOfMeasure] = useState<string>("");
  const [packaging, setPackaging] = useState<string>("");

  // ---------------- Dropdowns ----------------
  const [uoms, setUoms] = useState<UOMOption[]>([]);
  const [packageTypes, setPackageTypes] = useState<PackageOption[]>([]);

  const [selectedUOMObj, setSelectedUOMObj] = useState<UOMOption | null>(null);
  const [selectedPackageObj, setSelectedPackageObj] = useState<PackageOption | null>(null);
  const [description, setDescription] = useState<string>("");
  const [imageType, setImageType] = useState<string>("common");

  // ---------------- Selected items ----------------
  const locationState = (location.state as LocationState) || {};
  const selectedItemFromRoute = locationState?.selectedItem || null;
  const [selectedItem, setSelectedItem] = useState<ApiItem | null>(selectedItemFromRoute);

  // ✅ FIX: read incoming commonImages / imageType forwarded from AddVariantPage.
  //         Compute once at mount so the value is stable across re-renders.
  const forwardedCommonImages = React.useMemo(() => {
    if (locationState?.commonImages && locationState.commonImages.length > 0) {
      return locationState.commonImages;
    }
    // Fallback: parse from selectedItem.productimgs in case AddVariantPage didn't forward
    return parseCommonImagesFromItem(selectedItemFromRoute);
  }, [locationState?.commonImages, selectedItemFromRoute]);

  // ---------------- User info ----------------
  const SYS_USER = useSelector((state: RootState) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  const { variantTypes: dropdownVariantTypes, refetch: refetchDropdownData } = useDropdownData(COMPCODE, STAFFMAPLOCODE);

  // ----------------- Back Button ------------
  const goBackToView = (): void => navigate("/vAddVariant");

  const getItemDetails = (item: ApiItem | null) => {
    if (!item) return {};
    return {
      brandName: item.brandname,
      mainCategory: item.maincatname,
      category: item.catname,
      subCategory: item.subcatname,
      itemName: item.itemname,
      genericName: item.genericname,
    };
  };

  const itemDetails = getItemDetails(selectedItem);

  // Dummy states for variant section
  const [showAddType, setShowAddType] = useState<boolean>(false);
  const [newVariantName, setNewVariantName] = useState<string>("");
  const [newVariantType, setNewVariantType] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<VariantTypeOption | null>(null);
  const [variantValue, setVariantValue] = useState<string>("");
  const [variantData, setVariantData] = useState<VariantData>({});
  const [variantTypes, setVariantTypes] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    setSearchQuery(value || "");
  };

  const [data, setData] = useState<any[]>([]);

  // Existing variants
  const [existingVariantsRows, setExistingVariantsRows] = useState<ExistingVariantRow[]>([]);
  const [existingVariantPairsForCheck, setExistingVariantPairsForCheck] = useState<VariantPair[]>([]);
  const [existingCombinations, setExistingCombinations] = useState<VariantCombination[]>([]);
  const [existingVariantsLoading, setExistingVariantsLoading] = useState<boolean>(false);
  const [variantAlertOpen, setVariantAlertOpen] = useState<boolean>(false);
  const [variantAlertMessage, setVariantAlertMessage] = useState<string>("");
  const [variantAlertType, setVariantAlertType] = useState<"success" | "error" | "warning" | "info">("error");
  const [uniqueCombinationsDialogOpen, setUniqueCombinationsDialogOpen] = useState<boolean>(false);
  const [uniqueCombinationsToCreate, setUniqueCombinationsToCreate] = useState<VariantCombination[]>([]);

  const parseVariantDataToPairs = (variantInput: string | undefined | null): VariantPair[] => {
    if (variantInput === undefined || variantInput === null || variantInput === "") return [];
    const pairs: VariantPair[] = [];
    try {
      const str = typeof variantInput === "string" ? variantInput.trim() : String(variantInput);
      if (str.startsWith("[")) {
        let parsed = [];
        try {
          const cleaned = str.replace(/\\"/g, '"');
          parsed = JSON.parse(cleaned.startsWith('"') && cleaned.endsWith('"') ? cleaned.slice(1, -1) : cleaned);
        } catch (e) {
          parsed = JSON.parse(str);
        }
        const arr = Array.isArray(parsed) ? parsed : [];
        arr.forEach((v: any) => {
          const name = (v.variantname || v.variantName || "").trim();
          const val = (v.variantvalue || v.variantValue || "").trim();
          if (name && val) pairs.push({ variantName: name, value: val });
        });
      } else {
        const entries = str.split(";").map((v) => v.trim()).filter(Boolean);
        entries.forEach((entry) => {
          const [type, valuesStr] = entry.split(":").map((s) => s.trim());
          if (type && valuesStr) {
            valuesStr.split(",").map((v) => v.trim()).filter(Boolean).forEach((val) => {
              pairs.push({ variantName: type, value: val });
            });
          }
        });
      }
    } catch (err) {
      console.error("parseVariantDataToPairs error:", err);
    }
    return pairs;
  };

  const formatVariantDataAsDisplay = (variantInput: string | undefined | null): string => {
    const pairs = parseVariantDataToPairs(variantInput);
    if (pairs.length === 0) return "None";
    return pairs.map((p) => `${p.variantName}: ${p.value}`).join(", ");
  };

  const fetchExistingVariants = async (): Promise<void> => {
    if (!selectedItem) return;
    setExistingVariantsLoading(true);
    try {
      const params = { COMPCODE, LOCCODE: STAFFMAPLOCODE, STS: CON_STATUS_ACTIVE };
      const response = await externalgetApi(ITEM_API_01, "GET", params);
      let itemsList: ApiItem[] = [];
      if (response && Array.isArray(response)) itemsList = response;
      else if (response && typeof response === "object" && !Array.isArray(response))
        itemsList = Object.values(response);

      const main = selectedItem.maincatcode;
      const cat = selectedItem.catcode;
      const sub = selectedItem.subcatcode;
      const name = (selectedItem.itemname || "").toString().trim();

      const matchingItems = itemsList.filter(
        (item) =>
          String(item.maincatcode || "") === String(main || "") &&
          String(item.catcode || "") === String(cat || "") &&
          String(item.subcatcode || "") === String(sub || "") &&
          String((item.itemname || "").toString().trim()) === name
      );

      const allPairsForCheck: VariantPair[] = [];
      const existingCombos: VariantCombination[] = [];
      const rows: ExistingVariantRow[] = matchingItems.map((item, idx) => {
        const pairs = parseVariantDataToPairs(item.variantdata);
        pairs.forEach((p) => allPairsForCheck.push(p));
        const comboObj: VariantCombination = pairs.reduce((acc, p) => ({ ...acc, [p.variantName]: p.value }), {});
        if (Object.keys(comboObj).length > 0) existingCombos.push(comboObj);
        const variantDisplay = formatVariantDataAsDisplay(item.variantdata);
        return {
          id: `existing-${item.itemcode || idx}-${idx}`,
          index: idx + 1,
          variantDisplay,
        };
      });

      setExistingVariantsRows(rows);
      setExistingVariantPairsForCheck(allPairsForCheck);
      setExistingCombinations(existingCombos);
    } catch (error) {
      console.error("Error fetching existing variants:", error);
      setExistingVariantsRows([]);
      setExistingVariantPairsForCheck([]);
      setExistingCombinations([]);
    } finally {
      setExistingVariantsLoading(false);
    }
  };

  // Fetch UOM and Packaging dropdown data
  const fetchDropdowns = async (): Promise<void> => {
    const params = {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      STS: CON_STATUS_ACTIVE,
    };

    try {
      const uomRes = await externalgetApi(SETTING_API_05, "get", params);
      if (Array.isArray(uomRes)) {
        setUoms(
          uomRes.map((u: any) => ({
            label: u.measuringunit,
            value: u.measuringunitcode,
          }))
        );
      }

      const pkgRes = await externalgetApi(SETTING_API_06, "get", params);
      if (Array.isArray(pkgRes)) {
        setPackageTypes(
          pkgRes.map((p: any) => ({
            label: p.packagename,
            value: p.packagecode,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching dropdown data", error);
    }
  };

  const loadSelectedItemData = (): void => {
    if (!selectedItem) return;

    setGenericName(selectedItem.genericname || "");
    setModelName(selectedItem.modelname || "");
    setUnitPerPackage(String(selectedItem.packageunits || ""));
    setUnitOfMeasure(String(selectedItem.measuringunitcode || selectedItem.uomcode || ""));
    setPackaging(String(selectedItem.packagecode || selectedItem.packagingcode || ""));
    setDescription(selectedItem.descrip || "");
  };

  // ✅ FIXED: Sync UOM with fallback for codes not present in the loaded list
  const syncUOMSelection = (): void => {
    if (!unitOfMeasure) {
      setSelectedUOMObj(null);
      return;
    }

    const matchedUom = uoms.find(
      (u) => String(u.value) === String(unitOfMeasure)
    );

    if (matchedUom) {
      setSelectedUOMObj(matchedUom);
      return;
    }

    // Fallback: build a synthetic option from the selected item so the field isn't blank
    const fallbackLabel =
      (selectedItem?.measuringunitname as string) ||
      (selectedItem?.measuringunit as string) ||
      (selectedItem?.uomcode != null ? String(selectedItem.uomcode) : "") ||
      "";

    if (fallbackLabel) {
      setSelectedUOMObj({ label: fallbackLabel, value: unitOfMeasure });
    } else {
      setSelectedUOMObj(null);
    }
  };

  // ✅ FIXED: Sync Packaging with fallback for codes not present in the loaded list
  const syncPackagingSelection = (): void => {
    if (!packaging) {
      setSelectedPackageObj(null);
      return;
    }

    const matchedPkg = packageTypes.find(
      (p) => String(p.value) === String(packaging)
    );

    if (matchedPkg) {
      setSelectedPackageObj(matchedPkg);
      return;
    }

    // Fallback: build a synthetic option from the selected item so the field isn't blank
    const fallbackLabel =
      (selectedItem?.packagename as string) ||
      (selectedItem?.packagecode != null ? String(selectedItem.packagecode) : "") ||
      "";

    if (fallbackLabel) {
      setSelectedPackageObj({ label: fallbackLabel, value: packaging });
    } else {
      setSelectedPackageObj(null);
    }
  };

  // ------------- useEffect --------------

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (!dropdownVariantTypes?.length) return;
    setVariantTypes((prev: any[]) => {
      const apiCodes = new Set(dropdownVariantTypes.map((t: any) => t.code));
      const addedThisSession = prev.filter((t) => !apiCodes.has(t.code));
      return [...dropdownVariantTypes, ...addedThisSession];
    });
  }, [dropdownVariantTypes]);

  useEffect(() => {
    loadSelectedItemData();
  }, [selectedItem]);

  useEffect(() => {
    syncUOMSelection();
  }, [uoms, unitOfMeasure, selectedItem]);

  useEffect(() => {
    syncPackagingSelection();
  }, [packageTypes, packaging, selectedItem]);

  useEffect(() => {
    if (selectedItem) fetchExistingVariants();
  }, [selectedItem?.maincatcode, selectedItem?.catcode, selectedItem?.subcatcode, selectedItem?.itemname]);

  const allCurrentVariantPairs: VariantPair[] = [
    ...existingVariantPairsForCheck.map((p) => ({ variantName: p.variantName, value: p.value })),
    ...Object.entries(variantData).flatMap(([vn, values]) => (values || []).map((v) => ({ variantName: vn, value: v }))),
  ];

  const isVariantDuplicate = (variantName: string, value: string): boolean => {
    const v = (value || "").trim();
    if (!v || !variantName) return false;
    const key = `${(variantName || "").toLowerCase()}|${v.toLowerCase()}`;
    return allCurrentVariantPairs.some(
      (p) => `${(p.variantName || "").toLowerCase()}|${(p.value || "").toLowerCase()}` === key
    );
  };

  const generateCombinationsFromVariantData = (vData: VariantData): VariantCombination[] => {
    const entries = Object.entries(vData || {}).filter(([, values]) => Array.isArray(values) && values.length > 0);
    if (entries.length === 0) return [];

    const generate = (index: number, current: VariantCombination): VariantCombination[] => {
      if (index === entries.length) return [current];
      const [type, values] = entries[index];
      const results: VariantCombination[] = [];
      for (const val of values) {
        const next = { ...current, [type]: (val || "").trim() };
        if ((val || "").trim()) results.push(...generate(index + 1, next));
      }
      return results;
    };
    return generate(0, {});
  };

  const comboKey = (combo: VariantCombination): string => {
    return Object.keys(combo)
      .sort()
      .map((k) => `${(k || "").toLowerCase()}:${(combo[k] || "").toLowerCase()}`)
      .join("|");
  };

  const isExactMatch = (comboA: VariantCombination, comboB: VariantCombination): boolean => comboKey(comboA) === comboKey(comboB);

  const handleAddVariantTypeFromDialog = async (): Promise<void> => {
    const name = (newVariantName || "").trim();

    if (!name) {
      setVariantAlertType("error");
      setVariantAlertMessage("Please enter a variant name.");
      setVariantAlertOpen(true);
      return;
    }

    const isDuplicate = variantTypes?.some(
      (row) => (row.VARIANTNAME ?? row.variantname ?? row.label ?? "").toString().trim().toLowerCase() === name.toLowerCase()
    );

    if (isDuplicate) {
      setVariantAlertType("error");
      setVariantAlertMessage("This variant name already exists.");
      setVariantAlertOpen(true);
      return;
    }

    const payload = {
      VARIANTCODE: 0,
      COMPCODE: COMPCODE || "",
      LOCCODE: STAFFMAPLOCODE || "",
      VARIANTNAME: name,
      VARIANTDATATYPE: "Text",
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME || "",
      ENDATE: new Date().toISOString().slice(0, 10),
    };

    try {
      await externalpostApi(ITEM_CREATE_VARIANT_TYPE, payload);

      const nextCode = Math.max(0, ...(variantTypes || []).map((t) => t.code || 0)) + 1;

      setVariantTypes((prev: any[]) => [
        ...(prev || []),
        {
          code: nextCode,
          value: name.toLowerCase().replace(/\s+/g, ""),
          label: name,
          name: name,
          variantname: name,
          VARIANTNAME: name
        },
      ]);

      setNewVariantName("");
      setShowAddType(false);

      await refetchDropdownData();

      setVariantAlertType("success");
      setVariantAlertMessage("Variant type added successfully!");
      setVariantAlertOpen(true);

    } catch (err) {
      const error = err as ApiError;
      console.error("Createitemvarianttype error:", error);
      setVariantAlertType("error");
      setVariantAlertMessage(error?.response?.data?.message || "Failed to create variant type.");
      setVariantAlertOpen(true);
    }
  };

  const handleNextClick = (): void => {
    const newCombos = generateCombinationsFromVariantData(variantData);
    if (newCombos.length === 0) {
      setVariantAlertMessage("Add at least one variant type and value to create new combinations.");
      setVariantAlertType("warning");
      setVariantAlertOpen(true);
      return;
    }
    const uniqueNew = newCombos.filter((nc) => !existingCombinations.some((ec) => isExactMatch(nc, ec)));
    const deduped: VariantCombination[] = [];
    const seen = new Set<string>();
    uniqueNew.forEach((c) => {
      const key = comboKey(c);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(c);
    });
    setUniqueCombinationsToCreate(deduped);
    setUniqueCombinationsDialogOpen(true);
  };

  useEffect(() => {
    if (locationState?.returnFromItemImages) {
      if (locationState?.variants) {
        const restoredVariantData: VariantData = {};
        locationState.variants.forEach((v) => {
          restoredVariantData[v.type] = v.values || [];
        });
        setVariantData(restoredVariantData);
      }

      if (locationState?.formData) {
        const fd = locationState.formData;
        setModelName(fd.modelName || "");
        setUnitPerPackage(fd.unitsPerPackage || "");
        setDescription(fd.description || "");
        setUnitOfMeasure(fd.uom || "");
        setPackaging(fd.packageType || "");
      }

      if (locationState?.imageType) {
        setImageType(locationState.imageType);
      }
    }
  }, [locationState]);

  const leftGridColumns: GridColumn[] = [
    { field: "index", headerName: "#", width: 64 },
    {
      field: "variantDisplay",
      headerName: "Variant",
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        const fullText = params.value || "None";
        const displayText = fullText.length > 50 ? `${fullText.substring(0, 50)}...` : fullText;
        return (
          <Tooltip title={fullText} arrow placement="top">
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {displayText}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const uniqueCombosColumns: GridColumn[] = [
    { field: "index", headerName: "#", width: 64 },
    {
      field: "variantDisplay",
      headerName: "Variant",
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        const fullText = params.value || "";
        const displayText = fullText.length > 45 ? `${fullText.substring(0, 45)}...` : fullText;
        return (
          <Tooltip title={fullText} arrow placement="top">
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {displayText}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const uniqueCombosRows = uniqueCombinationsToCreate.map((combo, i) => ({
    id: `unique-${i}`,
    index: i + 1,
    variantDisplay: Object.entries(combo)
      .sort(([a], [b]) => (a || "").localeCompare(b || ""))
      .map(([k, v]) => `${k}: ${v}`)
      .join(", "),
  }));

  return (
    <>
      {/* Breadcrumb */}
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to All Items" arrow>
          <IconButton aria-label="back" sx={styles.btn_breadcrumb_back} onClick={goBackToView}>
            <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon} />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs separator="›" aria-label="breadcrumb" sx={styles.breadcrumb}>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            Home
          </Link>
          <Link sx={styles.breadcrumb_link} underline="hover">
            Item Management
          </Link>
          <Link sx={styles.breadcrumb_link_page} underline="hover" aria-current="page">
            Add Variant
          </Link>
        </Breadcrumbs>
      </Box>

      {/* App Bar */}
      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid
            container
            spacing={2}
            sx={{
              ...styles.appbar_grid_container,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Grid>
              <Typography sx={styles.appbar_title}>Add Variant</Typography>
            </Grid>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="contained"
                sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
                onClick={() => navigate("/vCreateItem")}
              >
                Create New Item
              </Button>
            </Box>
          </Grid>
        </AppBar>
      </Box>

      {/* ITEM DETAILS */}
      <Box
        sx={{
          backgroundColor: "transparent !important",
          boxShadow: "none !important",
          pb: 2,
          pt: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
          Item Details
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Brand Name"
              size="small"
              fullWidth
              disabled
              value={itemDetails.brandName || ""}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Item Main Category"
              size="small"
              fullWidth
              disabled
              value={itemDetails.mainCategory || ""}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Item Category"
              size="small"
              fullWidth
              disabled
              value={itemDetails.category || ""}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Item Subcategory"
              size="small"
              fullWidth
              disabled
              value={itemDetails.subCategory || ""}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Generic Name"
              size="small"
              fullWidth
              disabled
              value={genericName}
              onChange={(e) => setGenericName(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Item Name / Product Name"
              size="small"
              fullWidth
              disabled
              value={itemDetails.itemName || ""}
            />
          </Grid>

          {/* ✅ UOM — value now comes from selectedUOMObj with fallback */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={uoms}
              value={selectedUOMObj}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              getOptionLabel={(o) => o.label || ""}
              onChange={(e, v) => {
                setUnitOfMeasure(v?.value?.toString() || "");
                setSelectedUOMObj(v || null);
              }}
              renderInput={(params) => (
                <TextField {...params} required label="Unit Of Measure" size="small" />
              )}
            />
          </Grid>

          {/* ✅ Packaging Type — value now comes from selectedPackageObj with fallback */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={packageTypes}
              value={selectedPackageObj}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              getOptionLabel={(o) => o.label || ""}
              onChange={(e, v) => {
                setPackaging(v?.value?.toString() || "");
                setSelectedPackageObj(v || null);
              }}
              renderInput={(params) => (
                <TextField {...params} required label="Packaging Type" size="small" />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Model Name"
              size="small"
              fullWidth
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Unit Per Package"
              size="small"
              fullWidth
              required
              value={unitPerPackage}
              onChange={(e) => setUnitPerPackage(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 4 }} />

        {/* Variant Section */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>Existing Item Variants</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Variants from all items with same main category, category, sub category and item name.
              </Typography>

              <ViewDataGrid
                columns={leftGridColumns}
                rows={existingVariantsRows}
                gridid="id"
                displayActions={false}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>Add New Variant</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  size="small"
                  sx={{
                    ...styles.btn_primary,
                    ...styles.btn_appbar_add,
                    height: 36,
                  }}
                  onClick={() => setShowAddType(true)}
                >
                  Add Variant Type
                </Button>
              </Box>

              <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Autocomplete
                    options={variantTypes}
                    getOptionLabel={(opt) => opt.label || opt.name || ""}
                    value={selectedVariant}
                    onChange={(_, v) => setSelectedVariant(v)}
                    renderInput={(params) => <TextField {...params} size="small" label="Variant Type" />}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    value={variantValue}
                    onChange={(e) => setVariantValue(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    size="small"
                    fullWidth
                    sx={{
                      ...styles.btn_primary,
                      height: 36,
                    }}
                    onClick={() => {
                      if (!selectedVariant || !variantValue) return;
                      const trimmed = variantValue.trim();
                      if (!trimmed) return;
                      const typeKey = selectedVariant.label || selectedVariant.name || "";

                      const existingValues = variantData[typeKey] || [];
                      const isDuplicate = existingValues.some(
                        (v) => v.toLowerCase() === trimmed.toLowerCase()
                      );

                      if (isDuplicate) {
                        setVariantAlertType("error");
                        setVariantAlertMessage("This variant value already exists for this type.");
                        setVariantAlertOpen(true);
                        return;
                      }

                      setVariantData((prev) => ({
                        ...prev,
                        [typeKey]: [...(prev[typeKey] || []), trimmed],
                      }));

                      setVariantValue("");
                      setSelectedVariant(null);
                    }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>

              {Object.keys(variantData).length > 0 && (
                <Box sx={{ mt: 3, width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
                  >
                    Variants
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(variantData).map(([variantName, values]) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={variantName}>
                        <Card
                          sx={{
                            width: "100%",
                            border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                            borderRadius: 2,
                            minHeight: 120,
                            display: "flex",
                            flexDirection: "column",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              boxShadow: theme.shadows[4],
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, p: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                {variantName}
                              </Typography>
                              <Tooltip title="Remove variant type">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setVariantData((prev) => {
                                      const next = { ...prev };
                                      delete next[variantName];
                                      return next;
                                    });
                                  }}
                                  sx={{
                                    color: theme.palette.error.main,
                                    "&:hover": { backgroundColor: (theme.palette.error.light || "") + "20" },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Divider sx={{ my: 0.5 }} />
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                              {(values || []).map((val, valueIndex) => (
                                <Chip
                                  key={valueIndex}
                                  label={val}
                                  onDelete={() => {
                                    setVariantData((prev) => {
                                      const arr = [...(prev[variantName] || [])];
                                      arr.splice(valueIndex, 1);
                                      const next = { ...prev };
                                      if (arr.length === 0) delete next[variantName];
                                      else next[variantName] = arr;
                                      return next;
                                    });
                                  }}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    borderRadius: "16px",
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    backgroundColor: (theme.palette.primary.light || "") + "10",
                                    fontWeight: 500,
                                    "& .MuiChip-deleteIcon": {
                                      color: theme.palette.error.main,
                                      "&:hover": { color: theme.palette.error.dark },
                                    },
                                    "&:hover": { backgroundColor: (theme.palette.primary.light || "") + "20" },
                                  }}
                                />
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        <AddVariantTypeDialog
          open={showAddType}
          onClose={() => {
            setNewVariantName("");
            setShowAddType(false);
          }}
          existingVariantTypes={variantTypes ?? []}
          loading={false}
          variantName={newVariantName}
          onVariantNameChange={setNewVariantName}
          onAdd={handleAddVariantTypeFromDialog}
          styles={styles}
        />

        <Divider sx={{ my: 4 }} />

        {/* Image Options */}
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: "bold", mb: 2 }}>Item Images</Typography>
          <FormControl component="fieldset">
            <RadioGroup row value={imageType} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setImageType(e.target.value)}>
              <FormControlLabel value="common" control={<Radio />} label="Common Images" />
              <FormControlLabel value="itemwise" control={<Radio />} label="Item Specific Images" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ ...styles.btn_primary, ...styles.btn_add_next }}
            onClick={handleNextClick}
          >
            Next
          </Button>
        </Box>
      </Box>

      <SystemAlert
        open={variantAlertOpen}
        type={variantAlertType}
        message={variantAlertMessage}
        onClose={() => setVariantAlertOpen(false)}
      />

      {/* Dialog: Unique variant combinations */}
      <AppDialog
        open={uniqueCombinationsDialogOpen}
        onClose={() => setUniqueCombinationsDialogOpen(false)}
        title="Unique combinations to create as new items"
        maxWidth="sm"
        fullWidth
        cancelLabel="Back"
        confirmLabel="Proceed"
        confirmColor="primary"
        confirmDisabled={uniqueCombinationsToCreate.length === 0}
        onConfirm={() => {
          setUniqueCombinationsDialogOpen(false);
          const formData = {
            itemName: selectedItem?.itemname || "",
            description: description || "",
            modelName: modelName || "",
            unitsPerPackage: unitPerPackage || "0",
            mainCategory: selectedItem?.maincatcode?.toString() || "",
            category: selectedItem?.catcode?.toString() || "",
            subCategory: selectedItem?.subcatcode?.toString() || "",
            genericName: selectedItem?.genericcode?.toString() || "",
            packageType: packaging || "",
            uom: unitOfMeasure || "",
            brand: selectedItem?.brandcode?.toString() || "",
          };
          const variantsForImages = Object.entries(variantData).map(([type, values]) => ({
            type,
            values: values || [],
          }));

          // ✅ FIX: forward the previously uploaded common images instead of wiping them.
          console.log("✅ Forwarding commonImages to /item-images:", forwardedCommonImages);

          navigate("/item-images", {
            state: {
              formData,
              variants: variantsForImages,
              variantCombinations: uniqueCombinationsToCreate,
              combinationCount: uniqueCombinationsToCreate.length,
              commonImages: forwardedCommonImages,   // 👈 THE FIX
              imageType,                              // 👈 forward chosen image type
              itemId: 0,
              isEditMode: false,
              selectedItem: selectedItem || null,
              savedItemData: selectedItem || null,
              fromCreateVariant: true,
            },
          });
        }}
      >
        {uniqueCombinationsToCreate.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following variant combination(s) do not match any existing item and will be created
            as new items.
          </Typography>
        )}

        {uniqueCombinationsToCreate.length === 0 ? (
          <Typography color="text.secondary">
            All entered combinations already exist. No new items will be created.
          </Typography>
        ) : (
          <ViewDataGrid
            columns={uniqueCombosColumns}
            rows={uniqueCombosRows}
            gridid="id"
            displayActions={false}
          />
        )}
      </AppDialog>
    </>
  );
}

export default CreateVariant;